import type { DbClient } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import { DEFAULT_MAX_DISTANCE_MILES } from "@wags/discovery/pure.ts";
import { EDGE_COLORS } from "@wags/ui/edge-color.ts";
import {
    feedLenses,
    type FeedLens,
    type SearchSortValue,
} from "@/features/discovery";
import { prefetchWagsListings } from "@/features/listings";
import { resolveRouteApproximateLocation } from "@/features/viewer-location";
import type { InitialGeoContext } from "@/features/viewer-location";
import { prefetchInfiniteSearch } from "../search";
import { buildFindResultsDoomscrollDescriptor } from "./browse-route-helpers";
import {
    DoomscrollSearchSchema,
    type DoomscrollSearch,
} from "./discover-route.contract.ts";

export function getDiscoverLensSort(lens: FeedLens): SearchSortValue {
    if (lens === "needs-home-soon") return "oldest-listings";
    if (lens === "nearby") return "nearest-first";
    return "best-match";
}

export const browseDogsDiscoverRoute = {
    validateSearch: DoomscrollSearchSchema,
    loaderDeps: ({ search }: { search: DoomscrollSearch }) => search,
    ssr: "data-only" as const,
    beforeLoad: async ({
        context,
        search,
    }: {
        context: InitialGeoContext;
        search: DoomscrollSearch;
    }) => {
        if (search.source !== "feed") {
            throw redirect({
                to: "/discover",
                search: { ...search, source: "feed" },
                replace: true,
                state: true,
            });
        }
        const hasLocation = search.lat != null && search.lng != null;
        const locationFromEstimate = !hasLocation
            ? await resolveRouteApproximateLocation(context)
            : null;

        const shouldSetDefaultDistance =
            search.maxDistanceMiles === undefined &&
            (hasLocation || locationFromEstimate !== null);

        if (shouldSetDefaultDistance || locationFromEstimate) {
            throw redirect({
                to: "/discover",
                search: {
                    ...search,
                    maxDistanceMiles: shouldSetDefaultDistance
                        ? DEFAULT_MAX_DISTANCE_MILES
                        : search.maxDistanceMiles,
                    lat: locationFromEstimate?.lat ?? search.lat,
                    lng: locationFromEstimate?.lng ?? search.lng,
                },
                replace: true,
                state: true,
            });
        }
    },
    loader: async ({
        context,
        deps,
    }: {
        context: { dbClient: DbClient; queryClient: QueryClient };
        deps: DoomscrollSearch;
    }) => {
        if (deps.source !== "feed" || deps.dogId) return;

        const { lens: _lens, source: _source, ...findRouteSearch } = deps;
        const baseDescriptor = buildFindResultsDoomscrollDescriptor({
            ...findRouteSearch,
            source: undefined,
        });
        if (!baseDescriptor) return;

        const lens = deps.lens ?? "best-match";
        const data = await prefetchInfiniteSearch(
            context.queryClient,
            context.dbClient,
            {
                ...baseDescriptor.params,
                sortBy: getDiscoverLensSort(lens),
                feedContext:
                    feedLenses.find((option) => option.id === lens)?.label ??
                    "Dogs near you",
                page: 0,
                limit: 12,
            },
        ).catch(() => null);
        const listingIds =
            data?.pages.flatMap((page) =>
                page.results.map((listing) => listing.id),
            ) ?? [];
        return {
            listingHydrationPromise: prefetchWagsListings(
                context.dbClient,
                listingIds,
            ).catch(() => {}),
        };
    },
    head: () => ({
        meta: [
            {
                title: "Discover dogs — Wags",
            },
            {
                name: "theme-color",
                content: EDGE_COLORS.trueWhite,
            },
            {
                name: "color-scheme",
                content: "light",
            },
        ],
    }),
};
